import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth'

type CognitoToken = {
  toString: () => string
  payload?: {
    email?: unknown
    exp?: unknown
  }
}

type CognitoSession = {
  idToken: string
  refreshToken: string
  expirySeconds: number
  email: string
  name: string
  username: string
}

function valueToString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function expiryFromToken(token: CognitoToken | undefined): number {
  const exp = token?.payload?.exp
  if (typeof exp !== 'number') return 45 * 60
  return Math.max(0, exp - Math.floor(Date.now() / 1000))
}

function getStoredRefreshToken(username: string): string {
  if (typeof window === 'undefined') return ''
  const userPoolClientId = import.meta.env['VITE_AMPLIFY_USER_POOL_WEB_CLIENT_ID'] as string | undefined
  if (!userPoolClientId) return ''

  const preferredKey = `CognitoIdentityServiceProvider.${userPoolClientId}.${username}.refreshToken`
  return window.localStorage.getItem(preferredKey) || ''
}

export async function getCurrentCognitoSession(): Promise<CognitoSession> {
  const [user, attributes, session] = await Promise.all([getCurrentUser(), fetchUserAttributes(), fetchAuthSession()])
  const idToken = session.tokens?.idToken as CognitoToken | undefined
  const idTokenString = idToken?.toString() ?? ''
  const email = valueToString(attributes.email) || valueToString(idToken?.payload?.email)

  if (!idTokenString || !email) {
    throw new Error('No authenticated Cognito session')
  }

  return {
    idToken: idTokenString,
    refreshToken: getStoredRefreshToken(user.username),
    expirySeconds: expiryFromToken(idToken),
    email,
    name: valueToString(attributes.name),
    username: user.username,
  }
}
